import React, { useState, useEffect } from 'react';
import { PageHeader, Card, LoadingSpinner } from '../components/Layout';
import clsx from 'clsx';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

interface BinType {
  id: string;
  name: string;
  code: string;
  description: string | null;
  materialCategory: string | null;
  tracksHeight: boolean;
  tracksLength: boolean;
  defaultMaxHeight: number | null;
  defaultMaxLength: number | null;
  minUsableHeight: number | null;
  minUsableLength: number | null;
  colorCode: string | null;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// API helper
async function apiRequest(endpoint: string, options: RequestInit = {}) {
  const token = localStorage.getItem('admin_auth_token');
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(error.message || 'Request failed');
  }
  if (response.status === 204) return null;
  return response.json();
}

// Modal Component
function Modal({ isOpen, onClose, title, children }: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode
}) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />
        <div className="relative bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="border-b px-6 py-4 flex items-center justify-between sticky top-0 bg-white">
            <h2 className="text-xl font-bold text-gray-900">{title}</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="p-6">{children}</div>
        </div>
      </div>
    </div>
  );
}

// Material category options
const MATERIAL_CATEGORIES = [
  { value: '', label: 'None (General)' },
  { value: 'fabric', label: 'Fabric' },
  { value: 'extrusion', label: 'Extrusion' },
  { value: 'hardware', label: 'Hardware' },
  { value: 'packaging', label: 'Packaging' },
  { value: 'motor', label: 'Motor' },
  { value: 'other', label: 'Other' },
];

// Preset colors
const PRESET_COLORS = [
  { value: '#6B7280', label: 'Gray' },
  { value: '#3B82F6', label: 'Blue' },
  { value: '#10B981', label: 'Green' },
  { value: '#F59E0B', label: 'Amber' },
  { value: '#8B5CF6', label: 'Purple' },
  { value: '#EF4444', label: 'Red' },
  { value: '#EC4899', label: 'Pink' },
  { value: '#14B8A6', label: 'Teal' },
];

// Bin Type Form
function BinTypeForm({
  binType,
  onSave,
  onCancel,
}: {
  binType: Partial<BinType> | null;
  onSave: (data: Partial<BinType>) => Promise<void>;
  onCancel: () => void;
}) {
  const [form, setForm] = useState({
    name: binType?.name || '',
    code: binType?.code || '',
    description: binType?.description || '',
    materialCategory: binType?.materialCategory || '',
    tracksHeight: binType?.tracksHeight ?? false,
    tracksLength: binType?.tracksLength ?? false,
    defaultMaxHeight: binType?.defaultMaxHeight ?? '',
    defaultMaxLength: binType?.defaultMaxLength ?? '',
    minUsableHeight: binType?.minUsableHeight ?? '',
    minUsableLength: binType?.minUsableLength ?? '',
    colorCode: binType?.colorCode || '#6B7280',
    sortOrder: binType?.sortOrder ?? 0,
    isActive: binType?.isActive !== false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked :
              ['sortOrder', 'defaultMaxHeight', 'defaultMaxLength', 'minUsableHeight', 'minUsableLength'].includes(name)
                ? (value === '' ? '' : Number(value))
                : value
    }));
  };

  // Auto-generate code from name
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value;
    setForm((prev) => ({
      ...prev,
      name: newName,
      // Only auto-generate code if it's a new entry or code is empty
      code: !binType?.id || !prev.code
        ? newName.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '')
        : prev.code,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setError('Name is required');
      return;
    }
    if (!form.code.trim()) {
      setError('Code is required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onSave({
        name: form.name.trim(),
        code: form.code.trim().toLowerCase().replace(/\s+/g, '_'),
        description: form.description.trim() || null,
        materialCategory: form.materialCategory || null,
        tracksHeight: form.tracksHeight,
        tracksLength: form.tracksLength,
        defaultMaxHeight: form.defaultMaxHeight === '' ? null : Number(form.defaultMaxHeight),
        defaultMaxLength: form.defaultMaxLength === '' ? null : Number(form.defaultMaxLength),
        minUsableHeight: form.minUsableHeight === '' ? null : Number(form.minUsableHeight),
        minUsableLength: form.minUsableLength === '' ? null : Number(form.minUsableLength),
        colorCode: form.colorCode,
        sortOrder: Number(form.sortOrder),
        isActive: form.isActive,
      });
    } catch (err: any) {
      setError(err.message || 'Failed to save bin type');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg">{error}</div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="name"
            value={form.name}
            onChange={handleNameChange}
            required
            placeholder="e.g., Fabric Drops"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Code <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="code"
            value={form.code}
            onChange={handleChange}
            required
            placeholder="e.g., fabric_drops"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono"
          />
          <p className="text-xs text-gray-500 mt-1">Unique identifier (no spaces)</p>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
        <textarea
          name="description"
          value={form.description}
          onChange={handleChange}
          rows={2}
          placeholder="Optional description of what this bin type is for..."
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Material Category</label>
          <select
            name="materialCategory"
            value={form.materialCategory}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            {MATERIAL_CATEGORIES.map((cat) => (
              <option key={cat.value} value={cat.value}>{cat.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Color Code</label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              name="colorCode"
              value={form.colorCode}
              onChange={handleChange}
              className="w-10 h-10 rounded cursor-pointer border border-gray-300"
            />
            <select
              value={form.colorCode}
              onChange={(e) => setForm(prev => ({ ...prev, colorCode: e.target.value }))}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              {PRESET_COLORS.map((color) => (
                <option key={color.value} value={color.value}>{color.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="border-t pt-4">
        <h3 className="font-medium text-gray-900 mb-3">Drop Tracking Settings</h3>
        <div className="grid grid-cols-2 gap-4">
          <label className="flex items-center gap-2 cursor-pointer p-3 border rounded-lg hover:bg-gray-50">
            <input
              type="checkbox"
              name="tracksHeight"
              checked={form.tracksHeight}
              onChange={handleChange}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <div>
              <span className="text-sm font-medium text-gray-700">Tracks Height</span>
              <p className="text-xs text-gray-500">For fabric drops (cross-roll dimension)</p>
            </div>
          </label>

          <label className="flex items-center gap-2 cursor-pointer p-3 border rounded-lg hover:bg-gray-50">
            <input
              type="checkbox"
              name="tracksLength"
              checked={form.tracksLength}
              onChange={handleChange}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <div>
              <span className="text-sm font-medium text-gray-700">Tracks Length</span>
              <p className="text-xs text-gray-500">For extrusion/fabric drops</p>
            </div>
          </label>
        </div>
      </div>

      {(form.tracksHeight || form.tracksLength) && (
        <div className="border-t pt-4">
          <h3 className="font-medium text-gray-900 mb-3">Default Dimensions & Minimums</h3>
          <div className="grid grid-cols-2 gap-4">
            {form.tracksHeight && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Default Max Height (inches)
                  </label>
                  <input
                    type="number"
                    name="defaultMaxHeight"
                    value={form.defaultMaxHeight}
                    onChange={handleChange}
                    onWheel={(e) => e.currentTarget.blur()}
                    placeholder="e.g., 126"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Min Usable Height (inches)
                  </label>
                  <input
                    type="number"
                    name="minUsableHeight"
                    value={form.minUsableHeight}
                    onChange={handleChange}
                    onWheel={(e) => e.currentTarget.blur()}
                    placeholder="e.g., 12"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Drops shorter than this are waste</p>
                </div>
              </>
            )}
            {form.tracksLength && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Default Max Length (inches)
                  </label>
                  <input
                    type="number"
                    name="defaultMaxLength"
                    value={form.defaultMaxLength}
                    onChange={handleChange}
                    onWheel={(e) => e.currentTarget.blur()}
                    placeholder="e.g., 288"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Min Usable Length (inches)
                  </label>
                  <input
                    type="number"
                    name="minUsableLength"
                    value={form.minUsableLength}
                    onChange={handleChange}
                    onWheel={(e) => e.currentTarget.blur()}
                    placeholder="e.g., 84"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Drops shorter than this are waste</p>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 border-t pt-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Sort Order
          </label>
          <input
            type="number"
            name="sortOrder"
            value={form.sortOrder}
            onChange={handleChange}
            onWheel={(e) => e.currentTarget.blur()}
            min={0}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
          <p className="text-xs text-gray-500 mt-1">Lower numbers appear first</p>
        </div>

        <div className="flex items-center pt-6">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              name="isActive"
              checked={form.isActive}
              onChange={handleChange}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm font-medium text-gray-700">Active</span>
          </label>
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">
          Cancel
        </button>
        <button type="submit" disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
          {saving ? 'Saving...' : binType?.id ? 'Update' : 'Create'}
        </button>
      </div>
    </form>
  );
}

// Bin Types Page
export function BinTypesPage() {
  const [binTypes, setBinTypes] = useState<BinType[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingBinType, setEditingBinType] = useState<BinType | null>(null);
  const [showInactive, setShowInactive] = useState(false);

  const loadBinTypes = async () => {
    setLoading(true);
    try {
      const data = await apiRequest(`/bins/types?activeOnly=${!showInactive}`);
      setBinTypes(data || []);
    } catch (err) {
      console.error('Failed to load bin types:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBinTypes();
  }, [showInactive]);

  const handleSaveBinType = async (data: Partial<BinType>) => {
    if (editingBinType?.id) {
      await apiRequest(`/bins/types/${editingBinType.id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
    } else {
      await apiRequest('/bins/types', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    }
    setModalOpen(false);
    setEditingBinType(null);
    loadBinTypes();
  };

  const handleDeleteBinType = async (binType: BinType) => {
    if (!window.confirm(`Are you sure you want to delete "${binType.name}"?\n\nThis will fail if any bins are assigned to this type.`)) return;
    try {
      await apiRequest(`/bins/types/${binType.id}`, { method: 'DELETE' });
      loadBinTypes();
    } catch (err: any) {
      alert(err.message || 'Failed to delete bin type. Make sure no bins are using it.');
    }
  };

  const handleToggleActive = async (binType: BinType) => {
    try {
      await apiRequest(`/bins/types/${binType.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ isActive: !binType.isActive }),
      });
      loadBinTypes();
    } catch (err: any) {
      alert(err.message || 'Failed to update bin type');
    }
  };

  return (
    <>
      <PageHeader
        title="Bin Types"
        subtitle="Configure bin categories for organizing drops and materials"
        actions={
          <button
            onClick={() => {
              setEditingBinType(null);
              setModalOpen(true);
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Bin Type
          </button>
        }
      />

      <Card>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Bin Types Configuration</h2>
            <p className="text-sm text-gray-500">
              Define categories for bins to organize fabric drops, extrusion drops, hardware, etc.
            </p>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-600">Show inactive</span>
          </label>
        </div>

        {loading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner />
          </div>
        ) : binTypes.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p>No bin types found.</p>
            <p className="text-sm mt-1">Click "Add Bin Type" to create one.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-gray-500 text-sm border-b">
                  <th className="pb-3 font-medium w-8"></th>
                  <th className="pb-3 font-medium">Name</th>
                  <th className="pb-3 font-medium">Code</th>
                  <th className="pb-3 font-medium">Material</th>
                  <th className="pb-3 font-medium text-center">Tracks</th>
                  <th className="pb-3 font-medium text-center">Defaults</th>
                  <th className="pb-3 font-medium">Status</th>
                  <th className="pb-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {binTypes.map((binType) => (
                  <tr key={binType.id} className={clsx('border-b hover:bg-gray-50', !binType.isActive && 'opacity-60')}>
                    <td className="py-3">
                      <div
                        className="w-4 h-4 rounded-full"
                        style={{ backgroundColor: binType.colorCode || '#6B7280' }}
                        title={binType.colorCode || 'Default'}
                      />
                    </td>
                    <td className="py-3">
                      <span className="font-medium text-gray-900">{binType.name}</span>
                      {binType.description && (
                        <p className="text-xs text-gray-500 truncate max-w-xs">{binType.description}</p>
                      )}
                    </td>
                    <td className="py-3">
                      <code className="text-sm bg-gray-100 px-2 py-1 rounded">{binType.code}</code>
                    </td>
                    <td className="py-3 text-gray-600">
                      {binType.materialCategory || '-'}
                    </td>
                    <td className="py-3 text-center">
                      <div className="flex justify-center gap-1">
                        {binType.tracksHeight && (
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded" title="Tracks drop height">
                            H
                          </span>
                        )}
                        {binType.tracksLength && (
                          <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded" title="Tracks drop length">
                            L
                          </span>
                        )}
                        {!binType.tracksHeight && !binType.tracksLength && (
                          <span className="text-gray-400">-</span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 text-center text-xs text-gray-500">
                      {binType.defaultMaxHeight || binType.defaultMaxLength ? (
                        <div>
                          {binType.defaultMaxHeight && <div>H: {binType.defaultMaxHeight}"</div>}
                          {binType.defaultMaxLength && <div>L: {binType.defaultMaxLength}"</div>}
                        </div>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="py-3">
                      <button
                        onClick={() => handleToggleActive(binType)}
                        className={clsx(
                          'px-2 py-1 rounded-full text-xs font-medium transition-colors',
                          binType.isActive ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        )}
                      >
                        {binType.isActive ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="py-3">
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setEditingBinType(binType);
                            setModalOpen(true);
                          }}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteBinType(binType)}
                          className="text-red-600 hover:text-red-800"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start gap-2">
            <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="text-sm text-blue-800">
              <strong>Bin Type Settings:</strong>
              <ul className="mt-1 list-disc list-inside space-y-1">
                <li><strong>Tracks Height:</strong> Enable for fabric bins to track cross-roll drop height (used for seam panels)</li>
                <li><strong>Tracks Length:</strong> Enable for extrusion/fabric bins to track linear drop length</li>
                <li><strong>Min Usable:</strong> Drops smaller than this are automatically marked as waste</li>
                <li><strong>Default Max:</strong> Applied when creating new bins of this type</li>
              </ul>
            </div>
          </div>
        </div>
      </Card>

      {/* Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditingBinType(null);
        }}
        title={editingBinType ? 'Edit Bin Type' : 'Create Bin Type'}
      >
        <BinTypeForm
          binType={editingBinType}
          onSave={handleSaveBinType}
          onCancel={() => {
            setModalOpen(false);
            setEditingBinType(null);
          }}
        />
      </Modal>
    </>
  );
}

export default BinTypesPage;
